import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import config from "../config";

const DarkModeContext = createContext();
const DevModeContext = createContext();

export function ThemeProvider({ children }) {
  // dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });

  // dev mode state - automatically enabled if demo mode is on
  const [devMode, setDevMode] = useState(() => {
    // if demo mode is enabled in config, force dev mode on
    if (config.isDemoMode) {
      return true;
    }
    const saved = localStorage.getItem("devMode");
    return saved ? JSON.parse(saved) : false;
  });

  // dev actions state
  const [devModeActions, setDevModeActions] = useState([]);

  // dark mode persistence
  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
    if (darkMode) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
  }, [darkMode]);

  // dev mode persistence
  useEffect(() => {
    // don't save to localStorage if demo mode is forcing it on
    if (!config.isDemoMode) {
      localStorage.setItem("devMode", JSON.stringify(devMode));
    }
  }, [devMode]);

  const toggleDarkMode = () => setDarkMode((prev) => !prev);

  const toggleDevMode = () => {
    // if demo mode is on, don't allow toggling off
    if (config.isDemoMode) {
      console.log("Dev mode is locked on due to demo mode being enabled");
      return;
    }
    setDevMode((prev) => !prev);
  };

  // dev actions functions
  const registerDevAction = useCallback((action) => {
    setDevModeActions((prev) => [...prev, action]);
  }, []);

  const clearDevActions = useCallback(() => {
    setDevModeActions([]);
  }, []);

  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <DevModeContext.Provider
        value={{
          devMode,
          toggleDevMode,
          devModeActions,
          registerDevAction,
          clearDevActions,
          isDemoMode: config.isDemoMode, // expose demo mode status
        }}
      >
        {children}
      </DevModeContext.Provider>
    </DarkModeContext.Provider>
  );
}

export function useDarkMode() {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error("useDarkMode must be used within ThemeProvider");
  }
  return context;
}

export function useDevMode() {
  const context = useContext(DevModeContext);
  if (!context) {
    throw new Error("useDevMode must be used within ThemeProvider");
  }
  return context;
}
